import { useEffect, useState } from 'react';
import { toast } from "react-toastify";
import { Skeleton } from "@mui/material";
import { Panel } from '../../../layout/Panel';
import { Pagination } from '../../../layout/Pagination';
import { getList } from '../../../common/BackendProvider';
import { Link } from "react-router-dom";

const gradients = [
    'from-emerald-500 to-blue-500',
    'from-slate-800 to-emerald-500',
    'from-blue-600 to-cyan-400',
    'from-emerald-700 to-lime-400',
    'from-slate-900 to-blue-600',
    'from-green-600 to-emerald-300',
];

const formatPublishedDate = (value) => {
    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return '';
    }

    return date.toLocaleDateString("en-GB", {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    });
}

const NewsVisual = ({item, index}) => {
    if (item.image_url) {
        return (
            <img
                src={item.image_url}
                alt=""
                className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
            />
        );
    }

    return <div className={`h-full w-full bg-gradient-to-br ${gradients[index % gradients.length]}`} />;
}

export const NewsTab = ({company}) => {
    const [news, setNews] = useState([]);
    const [page, setPage] = useState(1);
    const [pages, setPages] = useState(1);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        getList("companies/" + company.symbol + "/news", {pagination: {page: page, perPage: 10}}).then((response) => {
            setNews(response.data.news);
            setPages(response.data.pages);
            setLoading(false);
        }).catch(
            () => {
                toast.error("Error while loading news", {});
                setLoading(false);
            }
        );
    }, [company, page]);

    const handlePageChange = (page) => {
        setPage(page);
    }

    return (
        <Panel className="border border-slate-200 shadow-sm">
            <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
                <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Company news feed</p>
                    <h2 className="mt-1 text-xl font-semibold text-slate-950">News Articles</h2>
                </div>
                <span className="rounded-full bg-emerald-50 px-3 py-1 text-sm font-semibold text-emerald-700">
                    {company.symbol}
                </span>
            </div>

            {loading && (
                <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
                    {Array.from({ length: 6 }, (_, index) => (
                        <div key={index} className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                            <Skeleton variant="rectangular" width="100%" height={170} sx={{ bgcolor: 'grey.50' }} />
                            <div className="space-y-3 p-4">
                                <Skeleton variant="text" width="38%" />
                                <Skeleton variant="text" width="92%" />
                                <Skeleton variant="text" width="74%" />
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {!loading && news?.length > 0 && (
                <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
                    {news.map((n, index) => {
                        const otherCompanies = n.companies?.filter((c) => c.symbol !== company.symbol) || [];

                        return (
                            <article key={n.id || index} className="group flex overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
                                <div className="flex w-full flex-col">
                                    <Link to={`/news/${n.id}`} className="block">
                                        <div className="relative h-44 overflow-hidden bg-slate-100">
                                            <NewsVisual item={n} index={index}/>
                                            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950/75 via-slate-950/35 to-transparent p-4">
                                                <div className="flex max-w-full flex-wrap items-center gap-2 text-[11px] font-bold uppercase leading-none tracking-[0.08em] text-white/90">
                                                    <span className="rounded-full bg-slate-950/35 px-2.5 py-1 backdrop-blur-sm">
                                                        {formatPublishedDate(n.published_at)}
                                                    </span>
                                                    {n.source && (
                                                        <span className="max-w-[12rem] truncate rounded-full bg-white/20 px-2.5 py-1 backdrop-blur-sm">
                                                            {n.source}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </Link>
                                    <div className="flex min-h-[210px] flex-1 flex-col p-4">
                                        <Link to={`/news/${n.id}`} className="block">
                                            <h3 className="line-clamp-2 text-base font-semibold leading-6 text-slate-950 group-hover:text-emerald-700">
                                                {n.headline}
                                            </h3>
                                            <p className="mt-3 line-clamp-4 text-sm leading-6 text-slate-600">
                                                {n.summary}
                                            </p>
                                        </Link>
                                        <div className="mt-auto flex flex-wrap items-center gap-2 pt-4">
                                            <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">{company.symbol}</span>
                                            {otherCompanies.slice(0, 4).map((c) => (
                                                <Link key={c.symbol} to={`/company/${c.symbol}`}>
                                                    <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-200">{c.symbol}</span>
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </article>
                        )
                    })}
                </div>
            )}

            {!loading && news?.length === 0 && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-8 text-center text-sm font-medium text-slate-500">
                    No news articles available for this company yet.
                </div>
            )}

            <div className="mt-5">
                <Pagination page={page} pages={pages} onPageChange={(page) => handlePageChange(page)} loading={loading}/>
            </div>
        </Panel>
    )
}
